import React from 'react';
import { Button, Card, List, Popconfirm, Tag, Typography } from 'antd';
import { observer } from 'mobx-react';
import FeatherIcon from 'feather-icons-react';

import QuoteStore from '../../stores/QuoteStore';
import { QuestionMark } from '../QuestionMark';

const { Text } = Typography;

const PREVIEW_WINDOW_NAME = 'root_quote_additional_file_preview';

function openFilePreviewPopup(url: string) {
    const width = Math.min(1024, window.screen.availWidth - 48);
    const height = Math.min(800, window.screen.availHeight - 48);
    const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2);
    const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2);
    const features = [
        `popup=yes`,
        `width=${width}`,
        `height=${height}`,
        `left=${left}`,
        `top=${top}`,
        'scrollbars=yes',
        'resizable=yes',
    ].join(',');
    const win = window.open(url, PREVIEW_WINDOW_NAME, features);
    if (win) {
        try {
            win.opener = null;
        } catch {
            /* ignore */
        }
        win.focus();
    }
}

export interface RootQuoteAdditionalFilesCardProps {
    setHasAdditionalFiles: (value: boolean) => void;
    handleUpdateQuoteField: (field: string, value: unknown, withLoading?: boolean) => Promise<void>;
    /** When removing the last additional file: reset form + optional fields (must match upload “remove all” behavior). */
    onAdditionalFilesEmpty?: () => void | Promise<void>;
}

/**
 * Staff requotes: lists files attached to the original (root) quote and lets staff copy them onto the current quote.
 */
export const RootQuoteAdditionalFilesCard = observer(
    ({
        setHasAdditionalFiles,
        handleUpdateQuoteField,
        onAdditionalFilesEmpty,
    }: RootQuoteAdditionalFilesCardProps) => {
        const quote = QuoteStore.quote;
        const rootFiles = quote?.root_quote_additional_files;

        if (!rootFiles?.length) {
            return null;
        }

        const currentIds = new Set((quote?.additional_files ?? []).map(f => f.id));
        const rootName = quote?.root_quote?.name;

        const addFileToQuote = async (fileId: number) => {
            if (currentIds.has(fileId)) {
                return;
            }
            const merged = [...Array.from(currentIds), fileId];
            setHasAdditionalFiles(true);
            await handleUpdateQuoteField('additional_files', merged);
        };

        const removeFileFromQuote = async (fileId: number) => {
            const remaining = (quote?.additional_files ?? []).filter(f => f.id !== fileId).map(f => f.id);
            if (remaining.length === 0) {
                await onAdditionalFilesEmpty?.();
            } else {
                await handleUpdateQuoteField('additional_files', remaining);
            }
        };

        return (
            <Card
                size="small"
                title={
                    <span>
                        Files from original quote
                        <QuestionMark>
                            These files were attached on the root quote ({rootName ?? 'original'}). You can add them to
                            this requote without re-uploading, or remove them from this quote when already linked.
                        </QuestionMark>
                    </span>
                }
                style={{ marginBottom: 16 }}
            >
                <List
                    size="small"
                    dataSource={rootFiles}
                    renderItem={file => {
                        const alreadyOnQuote = currentIds.has(file.id);
                        return (
                            <List.Item style={{ paddingLeft: 0, paddingRight: 0 }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        alignItems: 'flex-start',
                                        gap: 12,
                                        width: '100%',
                                    }}
                                >
                                    <FeatherIcon
                                        icon="file-text"
                                        size={20}
                                        style={{ flexShrink: 0, marginTop: 2 }}
                                    />
                                    {/* Reserve horizontal space for the name so actions never squeeze it to ~0 width */}
                                    <div
                                        style={{
                                            flex: '1 1 200px',
                                            minWidth: 'min(100%, 200px)',
                                            overflowWrap: 'break-word',
                                            wordBreak: 'normal',
                                        }}
                                    >
                                        <Text strong style={{ display: 'block', whiteSpace: 'normal' }}>
                                            {file.filename}
                                        </Text>
                                        {file.file ? (
                                            <div style={{ marginTop: 4 }}>
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    style={{ padding: 0, height: 'auto' }}
                                                    onClick={() => openFilePreviewPopup(file.file as string)}
                                                >
                                                    Preview
                                                </Button>
                                            </div>
                                        ) : null}
                                    </div>
                                    <div
                                        style={{
                                            flexShrink: 0,
                                            marginLeft: 'auto',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-end',
                                            gap: 6,
                                        }}
                                    >
                                        {alreadyOnQuote ? (
                                            <>
                                                <Tag color="default" style={{ margin: 0 }}>
                                                    On this quote
                                                </Tag>
                                                <Popconfirm
                                                    title="Remove this file from the current quote?"
                                                    okText="Remove"
                                                    cancelText="Cancel"
                                                    okButtonProps={{ danger: true }}
                                                    onConfirm={() => removeFileFromQuote(file.id)}
                                                >
                                                    <Button type="link" danger size="small" style={{ padding: 0 }}>
                                                        Remove from quote
                                                    </Button>
                                                </Popconfirm>
                                            </>
                                        ) : (
                                            <Button type="primary" size="small" onClick={() => addFileToQuote(file.id)}>
                                                Add to this quote
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </List.Item>
                        );
                    }}
                />
            </Card>
        );
    }
);
